/**
 * 1.7.5 — the charts of an event and of the Events list (D-1.7.5-1), as numbers. The page gives the words in the
 * person's language; the event report (the PDF for the Board) uses the English ones here. Pure: the same call
 * makes the chart on the screen and the one in the report.
 *
 * - An event: answered / yes / no / still waiting for an answer, day by day; tenants, volunteers and guests among
 *   the people coming; the places as a share (taken, left, on the waiting list, upstairs).
 * - The Events list: how many came to each event, and the months of the year as a line.
 */
import type { ChartSpec } from './charts';
import { addDaysToKey, serviceDateKey } from './dates';
import { HOLDS } from './event-places';
import type { EventDetail, EventView } from './events';

export interface EventChartWords {
  overTime: { title: string; note: string; category: string; answered: string; yes: string; no: string; waiting: string };
  who: { title: string; note: string; category: string; series: string; tenants: string; volunteers: string; guests: string };
  places: { title: string; note: string; category: string; series: string; taken: string; left: string; waiting: string; upstairs: string };
  unit: string;
}

export const EVENT_CHART_WORDS_EN: EventChartWords = {
  overTime: { title: 'Answers day by day', note: 'Everyone asked: who has answered, who said yes, who said no, and who has not answered yet.', category: 'Day', answered: 'Answered', yes: 'Yes', no: 'No', waiting: 'No answer yet' },
  who: { title: 'Who is coming', note: 'People who said yes and have a place.', category: 'Who', series: 'People', tenants: 'Tenants', volunteers: 'Volunteers', guests: 'Guests' },
  places: { title: 'Places', note: 'The tenant places of this event, in people.', category: 'Places', series: 'People', taken: 'Taken', left: 'Left', waiting: 'On the waiting list', upstairs: 'Upstairs (no seat)' },
  unit: 'people',
};

/** At most this many days on the line: an event open for longer shows its last days. */
export const OVER_TIME_MAX_DAYS = 45;

export interface OverTime {
  days: string[];
  answered: number[];
  yes: number[];
  no: number[];
  waiting: number[];
}

/**
 * Day by day, from the first invitation (or the first answer) to today or the event's day: how many lines had an
 * answer by the end of that day, and what it was. A changed answer is counted as what it says now, from the day
 * the person first answered — the page keeps no older answer to draw.
 */
export function answersOverTime(detail: EventDetail, todayKey: string): OverTime {
  const rows = detail.rows.filter((r) => !r.review);
  const dayOf = (iso: string | null) => (iso ? serviceDateKey(new Date(iso)) : null);
  const answeredOn = rows.map((r) => ({ day: r.coming === 'No Answer' ? null : (dayOf(r.respondedAt) ?? dayOf(r.lastChangedAt)), yes: r.coming === 'Yes' }));
  const starts = [dayOf(detail.event.invited.at), ...answeredOn.map((a) => a.day)].filter((d): d is string => !!d).sort();
  const end = todayKey < detail.event.dayKey ? todayKey : detail.event.dayKey;
  let start = starts[0] ?? end;
  if (start > end) start = end;
  const days: string[] = [];
  for (let d = start; d <= end && days.length < 400; d = addDaysToKey(d, 1)) days.push(d);
  const shown = days.slice(-OVER_TIME_MAX_DAYS);
  const upTo = (day: string, pick: (a: { day: string | null; yes: boolean }) => boolean) => answeredOn.filter((a) => a.day !== null && a.day <= day && pick(a)).length;
  return {
    days: shown,
    answered: shown.map((d) => upTo(d, () => true)),
    yes: shown.map((d) => upTo(d, (a) => a.yes)),
    no: shown.map((d) => upTo(d, (a) => !a.yes)),
    waiting: shown.map((d) => rows.length - upTo(d, () => true)),
  };
}

/** The people coming, in three kinds: tenants (with their household), volunteers, guests. */
export function whoIsComing(detail: EventDetail): { tenants: number; volunteers: number; guests: number } {
  const going = detail.rows.filter((r) => HOLDS.includes(r.status) || r.status === 'inDraw');
  const guests = going.reduce((s, r) => s + r.guestCount, 0);
  const people = (role: 'tenant' | 'volunteer') => going.filter((r) => r.role === role).reduce((s, r) => s + Math.max(1, r.attending) - r.guestCount, 0);
  return { tenants: people('tenant'), volunteers: people('volunteer'), guests };
}

/** The three charts of an event's page. `dayLabel` writes a day ("Oct 8"). The places chart is left out when the event has no limit. */
export function eventCharts(detail: EventDetail, todayKey: string, words: EventChartWords, dayLabel: (dayKey: string) => string): ChartSpec[] {
  const ot = answersOverTime(detail, todayKey);
  const who = whoIsComing(detail);
  const p = detail.event.three.places;
  const charts: ChartSpec[] = [
    {
      kind: 'line',
      title: words.overTime.title,
      note: words.overTime.note,
      categoryLabel: words.overTime.category,
      categories: ot.days.map(dayLabel),
      series: [
        { name: words.overTime.answered, values: ot.answered },
        { name: words.overTime.yes, values: ot.yes },
        { name: words.overTime.no, values: ot.no },
        { name: words.overTime.waiting, values: ot.waiting },
      ],
      unit: words.unit,
    },
    {
      kind: 'bar',
      title: words.who.title,
      note: words.who.note,
      categoryLabel: words.who.category,
      categories: [words.who.tenants, words.who.volunteers, words.who.guests],
      series: [{ name: words.who.series, values: [who.tenants, who.volunteers, who.guests] }],
      unit: words.unit,
    },
  ];
  if (p.tenantPlaces !== null) {
    const taken = p.guaranteed + p.winners + p.inDraw;
    const upstairs = detail.event.numbers.raffle ? 0 : p.upstairs;
    charts.push({
      kind: 'donut',
      title: words.places.title,
      note: words.places.note,
      categoryLabel: words.places.category,
      categories: [words.places.taken, words.places.left, words.places.waiting, ...(upstairs ? [words.places.upstairs] : [])],
      series: [{ name: words.places.series, values: [taken, Math.max(0, p.tenantPlaces - taken), p.waiting, ...(upstairs ? [upstairs] : [])] }],
      unit: words.unit,
    });
  }
  return charts;
}

/* ------------------------------------------------------------------------------------------------
 * The Events list: attendance by event, and the year as a line
 * ---------------------------------------------------------------------------------------------- */

export interface EventsListWords {
  byEvent: { title: string; note: string; category: string; expected: string; came: string };
  trend: { title: string; note: string; category: string; expected: string; came: string };
  unit: string;
}

export const EVENTS_LIST_WORDS_EN: EventsListWords = {
  byEvent: { title: 'Attendance by event', note: 'People with a place, and people checked in at the door.', category: 'Event', expected: 'Expected', came: 'Checked in' },
  trend: { title: 'The last twelve months', note: 'People with a place and people checked in, month by month.', category: 'Month', expected: 'Expected', came: 'Checked in' },
  unit: 'people',
};

/** The most events on the bars: the newest. */
export const BY_EVENT_MAX = 12;

/** The people a past event was for: with a place (plates upstairs included), and checked in at the door. */
const expectedOf = (e: EventView) => e.counts.attending + (e.plates && !e.numbers.raffle ? e.plates.upstairs : 0);

/**
 * `events`: past and upcoming together. The bars are the last twelve events up to today, oldest first; the line is
 * the twelve months ending with today's. `monthLabel` writes a month from its first day ("Oct 2026").
 */
export function eventsListCharts(events: readonly EventView[], todayKey: string, words: EventsListWords, monthLabel: (dayKey: string) => string): ChartSpec[] {
  const held = events.filter((e) => e.dayKey <= todayKey && e.status !== 'Cancelled' && !e.outing).sort((a, b) => a.dayKey.localeCompare(b.dayKey));
  const last = held.slice(-BY_EVENT_MAX);
  const months: string[] = [];
  let m = `${todayKey.slice(0, 6)}01`;
  for (let i = 0; i < 12; i++) {
    months.unshift(m);
    m = `${addDaysToKey(m, -1).slice(0, 6)}01`;
  }
  const inMonth = (month: string) => held.filter((e) => e.dayKey.slice(0, 6) === month.slice(0, 6));
  return [
    {
      kind: 'bar',
      title: words.byEvent.title,
      note: words.byEvent.note,
      categoryLabel: words.byEvent.category,
      categories: last.map((e) => e.name.replace(/^SECURITY-TEST\s+/i, '')),
      series: [
        { name: words.byEvent.expected, values: last.map(expectedOf) },
        { name: words.byEvent.came, values: last.map((e) => e.counts.checkedIn) },
      ],
      unit: words.unit,
    },
    {
      kind: 'line',
      title: words.trend.title,
      note: words.trend.note,
      categoryLabel: words.trend.category,
      categories: months.map(monthLabel),
      series: [
        { name: words.trend.expected, values: months.map((mo) => inMonth(mo).reduce((s, e) => s + expectedOf(e), 0)) },
        { name: words.trend.came, values: months.map((mo) => inMonth(mo).reduce((s, e) => s + e.counts.checkedIn, 0)) },
      ],
      unit: words.unit,
    },
  ];
}
